// DueDateCell — table cell that renders the proximity-aware due-date
// label from formatDueDate with the matching tone class. Exposes the
// raw ISO dueDate via `title` so hovering a cell reveals the exact
// date even when the label abbreviates it.
//
// Archived rows render the plain date with a muted tone and no
// proximity suffix — the row is no longer live, so urgency language
// ("8d overdue", "in 4d") misleads. Only the formatted date segment
// surfaces; the raw ISO still appears via `title` for hover.
//
// v3 surfacing modes: the `mode` prop tells the cell how to render
// when it's standing in for a parent row whose state is computed by
// deriveSurfacing. Modes:
//   oneoff               — default; existing proximity-aware behaviour.
//   parent-active-future — italic muted; prefix derived from the
//                          surfaced child's status: "Next" for
//                          not-started future, "In review" for
//                          submitted/under-review/late (the surfaced
//                          occurrence isn't NEXT, it's already
//                          waiting on customer action), "Revise"
//                          for revision-requested, "Resubmit" for
//                          rejected (still requires rework).
//   parent-urgent        — "Next: <date>" prefix, full red NON-italic.
//   parent-awaiting      — "Awaiting first occurrence", italic muted,
//                          no proximity suffix.
//   parent-completed     — "All N occurrences complete", muted slate,
//                          non-italic (terminal stable state).

import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { formatDueDate, type DueDateTone } from "./formatDueDate";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import type { DeliverableStatus } from "../../../../types";

export type DueDateCellMode =
  | "oneoff"
  | "parent-active-future"
  | "parent-urgent"
  | "parent-awaiting"
  | "parent-completed";

export interface DueDateCellProps {
  dueDate?: string;
  status: DeliverableStatus;
  /** Optional submittedAt — used to derive whether a submitted/under-
   *  review row is `late` (submission past dueDate) so the active-
   *  future prefix can read "In review" instead of the misleading
   *  "Next" for parent rows surfacing a submitted-late occurrence. */
  submittedAt?: string | null;
  today?: Date;
  /** When true, suppress the proximity suffix + urgency tone — the
   *  row is archived so the urgency framing no longer applies. */
  archived?: boolean;
  /** v3 surfacing mode (parent rows only). Default 'oneoff' preserves
   *  the existing per-row rendering. */
  mode?: DueDateCellMode;
  /** Required when mode === 'parent-completed' to render the
   *  "All N occurrences complete" copy. */
  totalOccurrences?: number;
}

/** Pick the user-facing prefix for parent-active-future based on the
 *  surfaced child's DERIVED status. "Next:" is misleading when the
 *  surfaced occurrence has already passed its due date and is sitting
 *  in the customer-review queue — better to lead with the action
 *  the row needs ("In review", "Revise", "Resubmit"). */
function activeFuturePrefix(
  status: DeliverableStatus,
  dueDate: string | undefined,
  today: Date | undefined,
  submittedAt: string | null | undefined,
): string {
  const derived = deriveDeliverableStatus(
    status,
    dueDate,
    today,
    submittedAt ?? null,
  );
  if (
    derived === "submitted" ||
    derived === "under-review" ||
    derived === "late"
  )
    return "In review";
  if (derived === "revision-requested") return "Revise";
  if (derived === "rejected") return "Resubmit";
  return "Next";
}

const TONE_CLASS: Record<DueDateTone, string> = {
  danger: "text-red-600 dark:text-red-400 font-medium",
  warning: "text-amber-600 dark:text-amber-400",
  neutral: "text-slate-900 dark:text-slate-100",
  muted: "text-slate-400 dark:text-slate-500",
};

// Whole-day delta in UTC days. Negative = overdue.
function daysBetweenUTC(from: Date, toIso: string): number {
  const a = Date.UTC(
    from.getUTCFullYear(),
    from.getUTCMonth(),
    from.getUTCDate(),
  );
  const due = new Date(`${toIso}T00:00:00Z`);
  const b = Date.UTC(
    due.getUTCFullYear(),
    due.getUTCMonth(),
    due.getUTCDate(),
  );
  return Math.round((b - a) / 86_400_000);
}

// Composes the screen-reader aria-label for each parent-row mode.
// Returns undefined for modes where the visible text already reads
// cleanly (oneoff) or when required context is missing, so callers
// can drop the aria-label attribute entirely.
function renderAriaLabel(
  mode: DueDateCellMode,
  dueDate: string | undefined,
  today: Date | undefined,
  totalOccurrences: number | undefined,
): string | undefined {
  if (mode === "parent-awaiting") {
    return "Awaiting first occurrence";
  }
  if (mode === "parent-completed") {
    const count = totalOccurrences ?? 0;
    const unit = count === 1 ? "occurrence" : "occurrences";
    return `All ${count} ${unit} complete`;
  }
  if (!dueDate) return undefined;
  const now = today ?? new Date();
  const days = daysBetweenUTC(now, dueDate);
  const longDate = formatDayMonthYear(dueDate);
  if (mode === "parent-urgent") {
    const overdueDays = Math.abs(days);
    const unit = overdueDays === 1 ? "day" : "days";
    return `Next occurrence: ${longDate}, ${overdueDays} ${unit} overdue`;
  }
  if (mode === "parent-active-future") {
    if (days <= 7) {
      const unit = days === 1 ? "day" : "days";
      return `Next occurrence: ${longDate}, due in ${days} ${unit}`;
    }
    const weeks = Math.round(days / 7);
    const unit = weeks === 1 ? "week" : "weeks";
    return `Next occurrence: ${longDate}, due in ${weeks} ${unit}`;
  }
  return undefined;
}

export default function DueDateCell({
  dueDate,
  status,
  submittedAt,
  today,
  archived,
  mode = "oneoff",
  totalOccurrences,
}: DueDateCellProps) {
  // Semantic aria-label that reads cleanly for assistive tech.
  // Returns undefined for the oneoff mode and for parent modes
  // missing the required context (no dueDate); React drops the
  // attribute when the prop is undefined.
  const ariaLabel = renderAriaLabel(mode, dueDate, today, totalOccurrences);

  // ─── v3 surfacing modes (parent rows) ───
  if (mode === "parent-awaiting") {
    return (
      <span
        data-testid="deliverable-due-date-cell"
        aria-label={ariaLabel}
        className={`${TONE_CLASS.muted} italic`}
      >
        Awaiting first occurrence
      </span>
    );
  }
  if (mode === "parent-completed") {
    const count = totalOccurrences ?? 0;
    return (
      <span
        data-testid="deliverable-due-date-cell"
        aria-label={ariaLabel}
        className={TONE_CLASS.muted}
      >
        All {count} {count === 1 ? "occurrence" : "occurrences"} complete
      </span>
    );
  }
  if (mode === "parent-urgent" && dueDate) {
    const { text } = formatDueDate(dueDate, status, today);
    return (
      <span
        data-testid="deliverable-due-date-cell"
        aria-label={ariaLabel}
        className={TONE_CLASS.danger}
        title={dueDate}
      >
        Next: {text}
      </span>
    );
  }
  if (mode === "parent-active-future" && dueDate) {
    const { text } = formatDueDate(dueDate, status, today);
    const prefix = activeFuturePrefix(status, dueDate, today, submittedAt);
    return (
      <span
        data-testid="deliverable-due-date-cell"
        aria-label={ariaLabel}
        className={`${TONE_CLASS.muted} italic`}
        title={dueDate}
      >
        {prefix}: {text}
      </span>
    );
  }

  // ─── oneoff (default) ───
  if (!dueDate) {
    return (
      <span
        data-testid="deliverable-due-date-cell"
        className={TONE_CLASS.muted}
      >
        —
      </span>
    );
  }

  if (archived) {
    return (
      <span
        data-testid="deliverable-due-date-cell"
        className={TONE_CLASS.muted}
        title={dueDate}
      >
        {formatDayMonthYear(dueDate)}
      </span>
    );
  }

  const { text, tone } = formatDueDate(dueDate, status, today);
  return (
    <span
      data-testid="deliverable-due-date-cell"
      className={TONE_CLASS[tone]}
      title={dueDate}
    >
      {text}
    </span>
  );
}
